import type {
  MsptTimeSeriesResponse,
  NetworkMsptTimeSeriesResponse,
  NetworkPopularityTimeSeriesResponse,
  NetworkTimeSeriesResponse,
  PopularityWindow,
  RankingsResponse,
  ServerAggregate,
  ServerFqdn,
  StarchartConfigResponse,
  StarchartDbInfoResponse,
  StarchartHealthResponse,
  StarchartServedResolution,
  StarchartServerStats,
  StarchartSummaryResponse,
  TimeSeriesResponse,
} from '../types/starchart';

const API_BASE_URL = 'https://popuapi.modded.space';

/**
 * How long a response stays usable, by kind of request.
 *
 * The upstream ingests every minute and recomputes rankings every 15, so nothing here needs to be
 * fresher than a minute. Historical windows (whose end is comfortably in the past) can be held
 * far longer than a window that ends "now".
 */
const TTL = {
  servers: 10 * 60_000,
  rankings: 5 * 60_000,
  config: 60 * 60_000,
  dbInfo: 5 * 60_000,
  /** A range ending in the recent past cannot change. */
  historicalSeries: 10 * 60_000,
  /** A range ending at "now" gains a sample every minute. */
  liveSeries: 30_000,
} as const;

/** A range whose end is older than this is treated as settled history. */
const LIVE_EDGE_MS = 5 * 60_000;

const MAX_CACHE_ENTRIES = 40;

/** Matches what `fetch` throws on abort, so callers can use one check for both. */
const abortError = () => new DOMException('The operation was aborted.', 'AbortError');

interface CacheEntry {
  at: number;
  value: unknown;
}

interface RequestOptions {
  signal?: AbortSignal;
  /** Skip the cache and overwrite it with a fresh response. */
  force?: boolean;
}

/**
 * StarChart popularity API client.
 *
 * The one client for `popuapi.modded.space` in ARGUS. Beyond the usual request/error handling it
 * carries three things the standalone StarChart site lacked and paid for:
 *
 *  - a TTL cache, so switching tabs or metrics does not refetch identical history;
 *  - in-flight deduplication, so a chart and its stat tiles asking for the same range in the same
 *    tick share one network request;
 *  - `/stats/aggregate` batching, which replaces the old site's one-request-per-server loop
 *    (58 requests, each pulling 24 hours of raw data, just to read a current player count).
 *
 * Server ids are sent with their original casing. `PROJINF.playcdu.co` and `projinf.playcdu.co`
 * are two genuinely distinct tracked series upstream, so lowercasing an id would silently query
 * the wrong one.
 */
class StarchartApiService {
  private cache = new Map<string, CacheEntry>();
  private inFlight = new Map<string, Promise<unknown>>();

  private async request<T>(endpoint: string, ttl: number, options: RequestOptions = {}): Promise<T> {
    const key = endpoint;
    const now = Date.now();

    if (!options.force) {
      const cached = this.cache.get(key);
      if (cached && now - cached.at < ttl) {
        return cached.value as T;
      }
    }

    // The underlying fetch is deliberately unsignalled and shared. A caller's `signal` cancels
    // that caller's interest, not the request itself: two components asking for the same URL in
    // the same tick must share one round trip, and one of them unmounting must not abort the
    // other. (Sharing a signalled promise is the subtle version of this bug — under React's
    // double-invoked development effects the second mount inherits the first mount's abort and
    // the page silently loads nothing.) A cancelled request still fills the cache, which is the
    // right outcome for small GETs.
    let shared = this.inFlight.get(key) as Promise<T> | undefined;

    if (!shared || options.force) {
      shared = (async () => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `StarChart API Error: ${response.status} ${response.statusText} - ${errorText}`,
          );
        }

        const value = (await response.json()) as T;
        this.cache.set(key, { at: Date.now(), value });
        this.evict();
        return value;
      })();

      this.inFlight.set(key, shared);
      // Detach the bookkeeping from the caller's chain so a rejection here is never unhandled.
      void shared.catch(() => undefined).finally(() => {
        if (this.inFlight.get(key) === shared) this.inFlight.delete(key);
      });
    }

    return this.withSignal(shared, options.signal);
  }

  /** Resolve with the shared request, or reject as soon as this caller loses interest. */
  private withSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(abortError());

    return new Promise<T>((resolve, reject) => {
      const onAbort = () => reject(abortError());
      signal.addEventListener('abort', onAbort, { once: true });
      promise.then(
        (value) => {
          signal.removeEventListener('abort', onAbort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener('abort', onAbort);
          reject(error);
        },
      );
    });
  }

  /** Oldest-first eviction; series payloads are large enough that the map needs a ceiling. */
  private evict() {
    if (this.cache.size <= MAX_CACHE_ENTRIES) return;
    const entries = Array.from(this.cache.entries()).sort((a, b) => a[1].at - b[1].at);
    for (const [key] of entries.slice(0, this.cache.size - MAX_CACHE_ENTRIES)) {
      this.cache.delete(key);
    }
  }

  private buildQueryString(
    params: Record<string, string | number | boolean | undefined | null>,
  ): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  /** A range ending in the past is settled; one ending now still gains samples. */
  private seriesTtl(end: Date): number {
    return Date.now() - end.getTime() > LIVE_EDGE_MS ? TTL.historicalSeries : TTL.liveSeries;
  }

  /** When the cached copy behind a request was fetched, for `CacheStatusIndicator`. */
  getCacheAge(endpoint: string): number | null {
    return this.cache.get(endpoint)?.at ?? null;
  }

  /** Drop everything, so a manual refresh really refetches. */
  clearCache() {
    this.cache.clear();
  }

  // ── Catalogue ─────────────────────────────────────────────────────────────

  /** Every FQDN StarChart tracks, in the API's own casing. */
  async getServers(options?: RequestOptions): Promise<string[]> {
    return this.request<string[]>('/servers', TTL.servers, options);
  }

  /**
   * Current ranking for a window, best first.
   *
   * Shorter windows list fewer servers — an inactive server drops out of the 24-hour ranking
   * while staying in the 28-day one. That absence is information, not an error.
   */
  async getRankings(
    window: PopularityWindow = '28d',
    options?: RequestOptions,
  ): Promise<RankingsResponse> {
    return this.request<RankingsResponse>(`/rankings/${window}`, TTL.rankings, options);
  }

  // ── Per-server history ────────────────────────────────────────────────────

  async getServerTimeseries(
    serverId: ServerFqdn,
    start: Date,
    end: Date,
    resolution: StarchartServedResolution,
    options?: RequestOptions,
  ): Promise<TimeSeriesResponse> {
    const qs = this.buildQueryString({
      start: start.toISOString(),
      end: end.toISOString(),
      resolution,
    });
    return this.request<TimeSeriesResponse>(
      `/servers/${encodeURIComponent(serverId)}/timeseries${qs}`,
      this.seriesTtl(end),
      options,
    );
  }

  /** Tick timings. Both `mspt` and `tps` are nullable per sample — never coerce them to zero. */
  async getServerMspt(
    serverId: ServerFqdn,
    start: Date,
    end: Date,
    resolution: StarchartServedResolution,
    options?: RequestOptions,
  ): Promise<MsptTimeSeriesResponse> {
    const qs = this.buildQueryString({
      start: start.toISOString(),
      end: end.toISOString(),
      resolution,
    });
    return this.request<MsptTimeSeriesResponse>(
      `/servers/${encodeURIComponent(serverId)}/mspt${qs}`,
      this.seriesTtl(end),
      options,
    );
  }

  /**
   * Server-side average, peak, minimum and percentiles for a range.
   *
   * Worth calling even though the same numbers could be reduced client-side: it saves walking
   * tens of thousands of samples in the browser, and it is the only source of percentiles.
   * Note it answers HTTP 500 for an unknown server, unlike `getServerStats`.
   */
  async getServerSummary(
    serverId: ServerFqdn,
    start: Date,
    end: Date,
    options?: RequestOptions,
  ): Promise<StarchartSummaryResponse> {
    const qs = this.buildQueryString({ start: start.toISOString(), end: end.toISOString() });
    return this.request<StarchartSummaryResponse>(
      `/servers/${encodeURIComponent(serverId)}/summary${qs}`,
      this.seriesTtl(end),
      options,
    );
  }

  /**
   * Lifetime reliability scores. Note the singular `/server/` path — the API is inconsistent here.
   * An unknown server answers HTTP 200 with every field null rather than a 404.
   */
  async getServerStats(
    serverId: ServerFqdn,
    options?: RequestOptions,
  ): Promise<StarchartServerStats> {
    return this.request<StarchartServerStats>(
      `/server/${encodeURIComponent(serverId)}/stats`,
      TTL.historicalSeries,
      options,
    );
  }

  // ── Batched and network-wide ──────────────────────────────────────────────

  /**
   * Player counts for many servers in one request.
   *
   * The single most valuable endpoint here: the whole network over a 90-minute window is about
   * 4 KB and one round trip. An unknown id comes back as an entry with an empty `data` array
   * rather than failing the batch.
   */
  async getAggregate(
    serverIds: ServerFqdn[],
    start: Date,
    end: Date,
    resolution: StarchartServedResolution,
    options?: RequestOptions,
  ): Promise<ServerAggregate[]> {
    if (serverIds.length === 0) return [];
    const qs = this.buildQueryString({
      server_ids: serverIds.join(','),
      start: start.toISOString(),
      end: end.toISOString(),
      resolution,
    });
    return this.request<ServerAggregate[]>(`/stats/aggregate${qs}`, this.seriesTtl(end), options);
  }

  async getNetworkTimeseries(
    start: Date,
    end: Date,
    resolution: StarchartServedResolution,
    options?: RequestOptions,
  ): Promise<NetworkTimeSeriesResponse> {
    const qs = this.buildQueryString({
      start: start.toISOString(),
      end: end.toISOString(),
      resolution,
    });
    return this.request<NetworkTimeSeriesResponse>(
      `/network/timeseries${qs}`,
      this.seriesTtl(end),
      options,
    );
  }

  async getNetworkMspt(
    start: Date,
    end: Date,
    resolution: StarchartServedResolution,
    options?: RequestOptions,
  ): Promise<NetworkMsptTimeSeriesResponse> {
    const qs = this.buildQueryString({
      start: start.toISOString(),
      end: end.toISOString(),
      resolution,
    });
    return this.request<NetworkMsptTimeSeriesResponse>(
      `/network/mspt${qs}`,
      this.seriesTtl(end),
      options,
    );
  }

  /**
   * Network-wide popularity over time.
   *
   * This works, unlike the per-server equivalent: `/rankings/{window}/timeseries/{server_id}` is
   * shadowed by a catch-all on `/rankings/**` that answers any path under it with the current
   * ranking list and HTTP 200. There is deliberately no method for that route — per-server
   * popularity history comes from ARGUS's own snapshots instead
   * (see `starchartRankHistoryApi`).
   */
  async getNetworkPopularity(
    window: PopularityWindow,
    start: Date,
    end: Date,
    options?: RequestOptions,
  ): Promise<NetworkPopularityTimeSeriesResponse> {
    const qs = this.buildQueryString({ start: start.toISOString(), end: end.toISOString() });
    return this.request<NetworkPopularityTimeSeriesResponse>(
      `/network/popularity/${window}${qs}`,
      this.seriesTtl(end),
      options,
    );
  }

  // ── Service status ────────────────────────────────────────────────────────

  /** Never cached — this is the liveness probe. */
  async getHealth(options?: RequestOptions): Promise<StarchartHealthResponse> {
    return this.request<StarchartHealthResponse>('/health', 0, { ...options, force: true });
  }

  async getDbInfo(options?: RequestOptions): Promise<StarchartDbInfoResponse> {
    return this.request<StarchartDbInfoResponse>('/db/info', TTL.dbInfo, options);
  }

  /** Ingest cadence and retention windows, so the UI can say what ranges are meaningful. */
  async getConfig(options?: RequestOptions): Promise<StarchartConfigResponse> {
    return this.request<StarchartConfigResponse>('/config', TTL.config, options);
  }
}

export const starchartApiService = new StarchartApiService();

/** True when a request was cancelled rather than genuinely failing. */
export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';
